import {FormInstance} from 'antd/es/form';
import {Moment} from 'moment';
import {ReactText} from 'react';

import {EFormMode} from 'components/form';
import {ICheckboxOption} from 'components/form/inputs/checkbox-group';
import {IDefaultFilters, IReferencePath, IUrlConfig} from 'entities/metadata';
import {EEntityType} from 'shared/constants/entities';
import {TRequestMessages, ERequestType} from 'shared/types/requests';
import {IDocumentSaveInfoFile} from 'store/slices/documents-uploading-slice';

import {IContextRawData} from '../context/context-types';

export interface IDataState {
    [key: string]: IEntityDataState;
}

export interface IEntityDataState {
    selectedSubsection: string;
    loading?: boolean;
    isError?: boolean;
    isEditable?: boolean;
    FORM_FIELDS_DATA?: IAdditionalOptionsData;
    PROPERTY_DATA?: IPropertyData;
    [EEntityType.TABLE]: ITableEntityData;
    [EEntityType.FORM_TABLE]: IFormTableEntityData;
    [EEntityType.FORM]: IFormEntityData;
    [EEntityType.EDIT_FORM]: IFormEntityData;
    [EEntityType.TABS]: IDirectoryEntityData;
    [EEntityType.TABS_WITH_FORM]: IDirectoryFormEntityData;
    [EEntityType.FILTER]: IFilterEntityData;
    [EEntityType.CONTEXT_FILTER]: IContextRawData;
    [EEntityType.TABLE_STATE]: ITableStateEntityData;
    [EEntityType.SEARCH]: ISearchEntityData;
    [EEntityType.TABS_WITH_REFERENCES]: IEntityData;
    [EEntityType.REFERENCE]: IReferenceEntityData;
    [EEntityType.NUMBER]: INumberEntityData;
    [EEntityType.EDITABLE_TABLE]: IModalEntityData;
    [EEntityType.REPORT]: IReportEntityData;
    [EEntityType.JASPER_REPORT]: IJasperReportEntityData;
    [EEntityType.LIST]: IListEntityData;
    [EEntityType.LIST_GRID]: IListGridEntityData;
    [EEntityType.MODAL]: { fileList: IDocumentSaveInfoFile[]; headers: string };
    [EEntityType.SUBSECTION_TABLE]: ISubsectionTableEntityData;
    [EEntityType.DASHBOARD]: IDashboardEntityData;
    [EEntityType.TABLE_REPORT]: IEntityData;
    [EEntityType.WIDGETS]: IEntityData;
}

export interface IEntityData {
}

export interface IReferenceEntityData extends IEntityData {
    rows?: TEntity[];
    placeholder?: string;
}

export interface INumberEntityData extends IEntityData {
    [key: string]: number;
}
export interface IComparison {
    sheet: string;
    addition: string;
    comparisonAttributes: string[];
    differenceAttributes: string[];

}

export interface ITableEntityData extends IEntityData {
    limit: number;
    offset: number;
    query?: string;
    page: number;
    ids?: string[];
    rows?: TEntity[];
    initialRows?: TEntity[];
    editRows?: TEntity[];
    params?: Record<string, any>;
    contextParameters?: TTableEntityBodyParameters;
    selectedRows?: TEntity[];
    selectedRowKeys?: (string | number)[];
    publishDocumentIds?: string[];
    isConfirmed?: boolean;
}

export interface IModalEntityData extends IEntityData {
    limit: number;
    offset: number;
    query?: string;
    page: number;
    rows?: TEntity[];
    params?: Record<string, any>;
    contextParameters?: TTableEntityBodyParameters;
    selectedRows?: TEntity[];
    selectedRowKeys?: (string | number)[];
}

export interface IDefaultTableState {
    sorter: {
        field?: ReactText;
        order?: ReactText;
    };
    pagination: {
        current?: number;
        pageSize?: number;
        // Приходит с сервера
        total?: number;
    };
}

export interface IDefaultTableStateFlat {
    paginationPageSize?: number;
    paginationCurrent?: number;
    paginationTotal?: number;
    sorterField?: ReactText;
    sorterOrder?: ReactText;
}

export interface IFormTableEntityData extends ITableEntityData {
}

export interface ISubsectionTableEntityData extends ITableEntityData {
}

export interface IReportEntityData extends ITableEntityData {
}

export interface IJasperReportEntityData extends ITableEntityData {
}

export interface IDirectoryEntityData extends IEntityData {
    tab?: string;
    rows?: TEntity[];
}

export interface IDirectoryFormEntityData extends IEntityData {
    tab?: string;
    tabs?: TEntity[];
}

export type TTableEntityBodyParameters = Record<string, any>;

export type TFormEntityDataType = TEntity & {
    settingDocumentTemplateMappingDtoList?: ICheckboxOption[];
    propertyValue: {
        id: number;
        levelCode: string;
        levelValue: number;
        propertyCode: string;
        propertyValue: string;
        version: number;
    };
    disabledFormFlag: boolean;
    disabledFormMessage: string;
};

export interface IFormEntityData extends IEntityData {
    id: string;
    data: TFormEntityDataType;
    initialData: TFormEntityDataType;
    formInstance?: FormInstance;
    isEditable?: boolean;
    formMode?: EFormMode;
    entityId?: string;
    formUsedAdditionalOptionId?: number;
    programSetPrograms?: [];
}

export interface IListEntityData {
    id: string;
    data: TEntity;
}

export interface IListGridEntityData extends IListEntityData {
    lists: Record<string, TEntity>[];
}

export interface IStatisticsEntry {
    key: string;
    value: number;
    label?: string;
}

export interface IDashboardEntry {
    dataTable: IStatisticsEntry[];
    recordsTotal?: number;
}

export interface IDashboardEntityData extends IEntityData {
    [key: string]: IDashboardEntry;
}

export interface IFilterEntityData extends IEntityData {
    data: TEntity;
    loading?: boolean;
}

export interface ISearchEntityData extends IEntityData {
    data: TEntity;
}

export interface ITableStateEntityData extends IEntityData {
    data: TEntity;
}

export interface IPropertyData {
    notificationDuration?: number;
}

export interface IAdditionalOptionsData {
    options: {
        [key: string]: any[] | undefined;
    };

    [key: string]: {
        formUsedAdditionalOptionId?: number;
    };
}

export interface ICreateActionGetFile {
    referenceUrl?: string;
    requestType?: ERequestType;
    data?: any;
    isMessage?: boolean;
    linkedEntityName?: string;
    body?: Record<string, any>;
    name?: string;
    urlConfig?: IUrlConfig;
    headers?: Record<string, string>;
    requestMessages?: TRequestMessages;
}

export interface IActionsSendApiRequest {
    referenceUrl?: string;
    requestType?: ERequestType;
    data?: Record<string, string>;
}

export interface ICreateActionHandlerForDelete {
    entityName: string;
    referenceUrl: string;
    requestType: ERequestType;
    itemList?: TEntity;
    itemListKey?: string;
    entityTypes?: EEntityType[];
    useUrl?: boolean;
    urlConfig?: IUrlConfig;
}

export interface ISendSelectedRowIds {
    entityName: string;
    referenceUrl: string;
    requestType: ERequestType;
    selectedRowIds: TEntity;
}

export interface ICreateActionHandlerForTableChange {
    entityName: string;
    newEntityTypeData: TEntity;
    url?: string;
    parentEntityType?: EEntityType;
    prefix?: string;
    disabledPagination?: boolean;
    skipIfFilterDataIsLoading?: boolean;
    urlConfig?: IUrlConfig;
}

export type TEntity = Record<string, TEntityValue>;

export type TEntityValue =
    string
    | number
    | boolean
    | Date
    | Moment
    | File
    | TFileList
    | IDefaultFilters[]
    | IComparison[]
    | undefined
    | null
    | Record<string, any>
    | TLookupValue

export type TFileList = TFileInfo[];

export type TFileInfo = {
    attachmentId: number;
    objectId: number;
    fileName: string;
    signatureFileName: string;
    loaSignFileName: string;
    fileSize: number;
    outputFileSize: string;
    fileHashCode: string;
}

export type TDataSetPayload = {
    data: IEntityData;
    entityName: string;
    entityType: EEntityType;
    loading?: boolean;
    isEditable?: boolean;
    entityId?: string;
    isError?: boolean;
};

export type TSetDataLoadingPayload = {
    entityName: string;
    loading: boolean;
}

export type TDataSetFormModePayload = {
    entityName: string;
    entityType: EEntityType;
    formMode?: EFormMode;
}

export interface ISetDocumentScansDataPayload {
    entityName: string;
    entityType: EEntityType | undefined;
    docId: string;
    scans: TFileInfo[];
}

export interface IRemoveDocumentScansPayload {
    entityName: string;
    entityType: EEntityType | undefined;
    docId: string;
    attachmentsIds: string[];
}

export type TDataSetIsEditablePayload = {
    entityName: string;
    entityType: EEntityType;
    isEditable?: boolean;
}

export type TSetFilterLoadingPayload = {
    entityName: string;
    loading: boolean;
}

export type TSetFilterPayload = {
    entityName: string;
    data: Record<string, string | null>;
}

export type TDataResetPayload = {
    entityName: string;
    entityType: string;
    loading?: boolean;
}
export type TResetInitialTableDataArgs = {
    entityName: string;
    entityType: string;
    loading?: boolean;
}

export type TDataSetInitialDataPayload = {
    entityName: string;
    isEditable?: boolean;
}

export type TUpdateFormDataPayload = {
    entityName: string;
    data?: Record<string, any>;
}

export type TResetFormDataPayload = {
    entityName: string;
}

export type TSetSelectedSubsection = {
    selectedSubsection: string;
}

export type TSetFormInstancePayload = {
    entityName: string;
    form: FormInstance;
}

export type TSetUsedAdditionalOptionIdPayload = {
    entityName: string;
    entityNameChild: string;
    optionId: string | number;
    entityType?: EEntityType;
}

export type TSetUsedAdditionalOptionsPayload = {
    data: IEntityData | undefined;
    entityName: string;
    entityType: string;
    loading?: boolean;
};

export type TResetUsedAdditionalOptionIdPayload = {
    entityName: string;
}

export type TLookupValue<LookupCode = string> = {
    id: number;
    meaning: string;
    lookupCode?: LookupCode;
    lookupType?: string;
    attribute1?: string;
}

export type TDataRequestTableParams = Partial<{}>

export type TDataResponseTable = TEntity[];

export type TDataResponseDirectory = TEntity[];

export type TOptionsTableType = {
    useContext?: boolean;
    useSearch?: boolean;
    paramsToBeConverted?: TDataRequestTableParams;
    params?: TDataRequestTableParams;
    filterParamKey?: string;
    disabledPagination?: boolean;
    sectionCode?: string;
    docTypeId?: string;
}

export type TOptionsTabsType = {
    useContext?: boolean;
}

export interface IReferenceResolverParams {
    referenceUrl?: string;
    path?: IReferencePath;
}

export interface IDeleteFormValue {
    isDelete: boolean;
    referenceUrl?: string;
    ids?: TEntity;
}

export enum EUserIdUrl {
    news = 'news',
    settingsUser = 'settings.user.settings.mailing',
    rolesAndOrganizations = 'settings.user.settings.roles.and.organizations',
}

export enum EPropertyCode {
    REQUIRED_EDS_WHEN_DOC_PUBLISH = 'REQUIRED_EDS_WHEN_DOC_PUBLISH',
    REQUIRED_LOA_SGN_WHEN_DOC_PUBLISH = 'REQUIRED_LOA_SGN_WHEN_DOC_PUBLISH',
    PUBLIC = 'PUBLIC',
    DELAYED_PUBLICATION = 'DELAYED_PUBLICATION'
}

export interface IDatePickerState {
    [id: string]: boolean;
}

export interface IRequestButtonBodyData {
    ids: (string | number)[];
    userName?: string| undefined;
}

export interface ISendRequestCopyResponseRequirementBodyData {
    ids: (string | number)[];
    docId: string;
}
